import { createContext, useContext, useEffect, ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { agencyConfigApi } from '@/lib/api'
import { useTheme } from '@/contexts/ThemeContext'
import { useAuth } from '@/contexts/AuthContext'
import type { AgencyConfiguration } from '@/types'

interface AgencyThemeContextType {
  config: AgencyConfiguration | undefined
  isLoading: boolean
  agencyName: string
  logoUrl: string | null
  backgroundColor: string
  hamburgerMenuColor: string
  sidebarTextColor: string
  primaryColor: string
  primaryForegroundColor: string
  secondaryColor: string
  secondaryForegroundColor: string
  accentColor: string
  textColor: string
  timezone: string
}

const AgencyThemeContext = createContext<AgencyThemeContextType | undefined>(undefined)

// Convert hex color to HSL values (without the hsl() wrapper, just "h s% l%")
function hexToHSL(hex: string): string {
  // Remove # if present
  hex = hex.replace(/^#/, '')

  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16) / 255
  const g = parseInt(hex.substring(2, 4), 16) / 255
  const b = parseInt(hex.substring(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  // Return in the format Shadcn expects: "h s% l%"
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

// Create a lighter version of a color for backgrounds
function lightenColor(hex: string, amount: number = 0.9): string {
  hex = hex.replace(/^#/, '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Blend with white
  const newR = Math.round(r + (255 - r) * amount)
  const newG = Math.round(g + (255 - g) * amount)
  const newB = Math.round(b + (255 - b) * amount)

  const newHex = `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
  return hexToHSL(newHex)
}

// Blueprint Desk Default Color Palette
const DEFAULT_COLORS = {
  backgroundColor: '#F7F7F7',          // Light gray background
  primaryColor: '#001D39',             // Deep navy - buttons, links, focus rings
  primaryForegroundColor: '#FFFFFF',   // White text on primary buttons
  secondaryColor: '#7BBDE8',           // Light blue - secondary buttons, muted backgrounds
  secondaryForegroundColor: '#001D39', // Deep navy text on secondary buttons
  accentColor: '#438CC4',              // Medium blue - hover states, highlights
  textColor: '#001D39',                // Deep navy - main text color
  hamburgerMenuColor: '#001D39',       // Deep navy - sidebar background
  sidebarTextColor: '#FFFFFF',         // White text on sidebar
}

// Super Admin Portal Color Palette - Deep navy theme
const SUPER_ADMIN_COLORS = {
  backgroundColor: '#F7F7F7',          // Light gray background
  primaryColor: '#001D39',             // Deep navy - buttons, links, focus rings
  primaryForegroundColor: '#FFFFFF',   // White text on primary buttons
  secondaryColor: '#7BBDE8',           // Light blue - secondary buttons, muted backgrounds
  secondaryForegroundColor: '#001D39', // Deep navy text on secondary buttons
  accentColor: '#438CC4',              // Medium blue - hover states, highlights
  textColor: '#001D39',                // Deep navy - main text color
  hamburgerMenuColor: '#001D39',       // Deep navy - sidebar background for super admin
  sidebarTextColor: '#FFFFFF',         // White text on sidebar
}

// Dark mode color constants - deep navy background
const DARK_MODE_COLORS = {
  backgroundColor: '#001D39',          // Deep navy background
  hamburgerMenuColor: '#001D39',       // Deep navy sidebar
  sidebarTextColor: '#FFFFFF',         // White text on sidebar
  textColor: '#F7F7F7',                // Light gray text for dark backgrounds
  primaryColor: '#7BBDE8',             // Light blue primary for dark mode
  primaryForegroundColor: '#001D39',   // Deep navy text on primary buttons
  secondaryColor: '#314A7C',           // Navy blue secondary
  secondaryForegroundColor: '#7BBDE8', // Light blue text on secondary buttons
  accentColor: '#438CC4',              // Medium blue accent
}

// localStorage key for caching theme
const THEME_CACHE_KEY = 'agencyThemeCache'

// Cached theme interface
interface CachedTheme {
  backgroundColor?: string
  hamburgerMenuColor?: string
  sidebarTextColor?: string
  primaryColor?: string
  primaryForegroundColor?: string
  secondaryColor?: string
  secondaryForegroundColor?: string
  accentColor?: string
  textColor?: string
}

// Get cached theme from localStorage
function getCachedTheme(): CachedTheme | null {
  try {
    const cached = localStorage.getItem(THEME_CACHE_KEY)
    if (cached) {
      return JSON.parse(cached)
    }
  } catch {
    // Ignore errors
  }
  return null
}

// Save theme to localStorage for instant loading on next visit
function saveThemeToCache(config: AgencyConfiguration) {
  try {
    const cache: CachedTheme = {
      backgroundColor: config.background_color,
      hamburgerMenuColor: config.hamburger_menu_color,
      sidebarTextColor: config.sidebar_text_color,
      primaryColor: config.primary_color,
      primaryForegroundColor: config.primary_foreground_color,
      secondaryColor: config.secondary_color,
      secondaryForegroundColor: config.secondary_foreground_color,
      accentColor: config.accent_color,
      textColor: config.text_color,
    }
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(cache))
  } catch {
    // Ignore errors
  }
}

interface AgencyThemeProviderProps {
  children: ReactNode
}

export function AgencyThemeProvider({ children }: AgencyThemeProviderProps) {
  const { isDark } = useTheme()
  const { user, isSuperAdmin } = useAuth()

  // Get cached theme for immediate use
  const cachedTheme = getCachedTheme()

  // Include tenant_id in query key so config is refetched when tenant changes
  // Don't fetch agency config for super admins - they use default super admin colors
  const { data: config, isLoading } = useQuery({
    queryKey: ['agencyConfig', user?.tenant_id],
    queryFn: () => agencyConfigApi.get(),
    staleTime: 1 * 60 * 1000, // Cache for 1 minute
    refetchOnWindowFocus: true,
    enabled: !!user && !isSuperAdmin, // Only fetch when user is authenticated and not super admin
  })

  // Save theme to cache when config is loaded
  useEffect(() => {
    if (config) {
      saveThemeToCache(config)
    }
  }, [config])

  useEffect(() => {
    const root = document.documentElement

    if (isDark) {
      // In dark mode, use dark mode colors
      const bgColor = DARK_MODE_COLORS.backgroundColor
      const textColor = DARK_MODE_COLORS.textColor
      const primaryColor = DARK_MODE_COLORS.primaryColor
      const primaryFgColor = DARK_MODE_COLORS.primaryForegroundColor
      const secondaryColor = DARK_MODE_COLORS.secondaryColor
      const secondaryFgColor = DARK_MODE_COLORS.secondaryForegroundColor
      const accentColor = DARK_MODE_COLORS.accentColor

      root.style.setProperty('--background', hexToHSL(bgColor))
      root.style.setProperty('--foreground', hexToHSL(textColor))
      root.style.setProperty('--card', '210 80% 15%')
      root.style.setProperty('--card-foreground', hexToHSL(textColor))
      root.style.setProperty('--popover', '210 80% 15%')
      root.style.setProperty('--popover-foreground', hexToHSL(textColor))
      root.style.setProperty('--primary', hexToHSL(primaryColor))
      root.style.setProperty('--primary-foreground', hexToHSL(primaryFgColor))
      root.style.setProperty('--secondary', hexToHSL(secondaryColor))
      root.style.setProperty('--secondary-foreground', hexToHSL(secondaryFgColor))
      root.style.setProperty('--muted', '220 43% 20%')
      root.style.setProperty('--muted-foreground', '220 10% 50%')
      root.style.setProperty('--accent', hexToHSL(accentColor))
      root.style.setProperty('--accent-foreground', '0 0% 100%')
      root.style.setProperty('--destructive', '342 80% 45%')
      root.style.setProperty('--destructive-foreground', '0 0% 100%')
      root.style.setProperty('--border', '220 43% 25%')
      root.style.setProperty('--input', '220 43% 25%')
      root.style.setProperty('--ring', hexToHSL(primaryColor))
    } else {
      // In light mode, use agency config colors or defaults
      const bgColor = config?.background_color || DEFAULT_COLORS.backgroundColor
      const textColor = config?.text_color || DEFAULT_COLORS.textColor
      const primaryColor = config?.primary_color || DEFAULT_COLORS.primaryColor
      const primaryFgColor = config?.primary_foreground_color || DEFAULT_COLORS.primaryForegroundColor
      const secondaryColor = config?.secondary_color || DEFAULT_COLORS.secondaryColor
      const secondaryFgColor = config?.secondary_foreground_color || DEFAULT_COLORS.secondaryForegroundColor
      const accentColor = config?.accent_color || DEFAULT_COLORS.accentColor

      root.style.setProperty('--background', hexToHSL(bgColor))
      root.style.setProperty('--foreground', hexToHSL(textColor))
      root.style.setProperty('--card', '0 0% 100%')
      root.style.setProperty('--card-foreground', hexToHSL(textColor))
      root.style.setProperty('--popover', '0 0% 100%')
      root.style.setProperty('--popover-foreground', hexToHSL(textColor))
      root.style.setProperty('--primary', hexToHSL(primaryColor))
      root.style.setProperty('--primary-foreground', hexToHSL(primaryFgColor))
      // For secondary, use a lightened version for background
      const lightSecondary = lightenColor(secondaryColor, 0.85)
      root.style.setProperty('--secondary', lightSecondary)
      root.style.setProperty('--secondary-foreground', hexToHSL(secondaryFgColor))
      root.style.setProperty('--muted', lightSecondary)
      root.style.setProperty('--muted-foreground', '220 9% 65%')
      // For accent, use a lightened version for background
      const lightAccent = lightenColor(accentColor, 0.85)
      root.style.setProperty('--accent', lightAccent)
      root.style.setProperty('--accent-foreground', hexToHSL(accentColor))
      root.style.setProperty('--destructive', '342 100% 59%')
      root.style.setProperty('--destructive-foreground', '0 0% 100%')
      root.style.setProperty('--border', '204 30% 85%')
      root.style.setProperty('--input', '204 30% 85%')
      root.style.setProperty('--ring', hexToHSL(primaryColor))
    }
  }, [config, isDark])

  // Get colors from config or defaults
  // In dark mode, always use dark mode colors
  // For super admins, use super admin colors
  // In light mode, use agency config colors or cached colors or defaults
  let backgroundColor: string
  let hamburgerMenuColor: string
  let sidebarTextColor: string
  let primaryColor: string
  let primaryForegroundColor: string
  let secondaryColor: string
  let secondaryForegroundColor: string
  let accentColor: string
  let textColor: string

  if (isDark) {
    // Always use dark mode colors
    backgroundColor = DARK_MODE_COLORS.backgroundColor
    hamburgerMenuColor = DARK_MODE_COLORS.hamburgerMenuColor
    sidebarTextColor = DARK_MODE_COLORS.sidebarTextColor
    textColor = DARK_MODE_COLORS.textColor
    primaryColor = DARK_MODE_COLORS.primaryColor
    primaryForegroundColor = DARK_MODE_COLORS.primaryForegroundColor
    secondaryColor = DARK_MODE_COLORS.secondaryColor
    secondaryForegroundColor = DARK_MODE_COLORS.secondaryForegroundColor
    accentColor = DARK_MODE_COLORS.accentColor
  } else if (isSuperAdmin) {
    // Super admin uses dedicated super admin colors
    backgroundColor = SUPER_ADMIN_COLORS.backgroundColor
    hamburgerMenuColor = SUPER_ADMIN_COLORS.hamburgerMenuColor
    sidebarTextColor = SUPER_ADMIN_COLORS.sidebarTextColor
    textColor = SUPER_ADMIN_COLORS.textColor
    primaryColor = SUPER_ADMIN_COLORS.primaryColor
    primaryForegroundColor = SUPER_ADMIN_COLORS.primaryForegroundColor
    secondaryColor = SUPER_ADMIN_COLORS.secondaryColor
    secondaryForegroundColor = SUPER_ADMIN_COLORS.secondaryForegroundColor
    accentColor = SUPER_ADMIN_COLORS.accentColor
  } else {
    // In light mode, use config colors, then cached colors, then defaults
    backgroundColor = config?.background_color || cachedTheme?.backgroundColor || DEFAULT_COLORS.backgroundColor
    hamburgerMenuColor = config?.hamburger_menu_color || cachedTheme?.hamburgerMenuColor || DEFAULT_COLORS.hamburgerMenuColor
    sidebarTextColor = config?.sidebar_text_color || cachedTheme?.sidebarTextColor || DEFAULT_COLORS.sidebarTextColor
    primaryColor = config?.primary_color || cachedTheme?.primaryColor || DEFAULT_COLORS.primaryColor
    primaryForegroundColor = config?.primary_foreground_color || cachedTheme?.primaryForegroundColor || DEFAULT_COLORS.primaryForegroundColor
    secondaryColor = config?.secondary_color || cachedTheme?.secondaryColor || DEFAULT_COLORS.secondaryColor
    secondaryForegroundColor = config?.secondary_foreground_color || cachedTheme?.secondaryForegroundColor || DEFAULT_COLORS.secondaryForegroundColor
    accentColor = config?.accent_color || cachedTheme?.accentColor || DEFAULT_COLORS.accentColor
    textColor = config?.text_color || cachedTheme?.textColor || DEFAULT_COLORS.textColor
  }

  const value: AgencyThemeContextType = {
    config,
    isLoading,
    agencyName: config?.agency_name || 'Blueprint Desk',
    logoUrl: config?.logo_url || null,
    backgroundColor,
    hamburgerMenuColor,
    sidebarTextColor,
    primaryColor,
    primaryForegroundColor,
    secondaryColor,
    secondaryForegroundColor,
    accentColor,
    textColor,
    timezone: config?.timezone || 'UTC',
  }

  return (
    <AgencyThemeContext.Provider value={value}>
      {children}
    </AgencyThemeContext.Provider>
  )
}

export function useAgencyTheme() {
  const context = useContext(AgencyThemeContext)
  if (context === undefined) {
    throw new Error('useAgencyTheme must be used within an AgencyThemeProvider')
  }
  return context
}

